import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Dimmer, Loader, Segment, Tab, TextArea } from "semantic-ui-react";
import { selectCurrentUserAdmin, selectCurrentUserApiToken, selectCurrentUserId, selectCurrentUserName } from "../slices/currentUser/currentUserSlice";
import { useNavigate, useParams } from "react-router-dom";
import { changesCleared, selectChangedResourcesList, selectChangedResourcesListStatus } from "../slices/changedResourcesList/changedResourcesListSlice";
import { fetchResourceCollection, selectAllResources, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import NavigationSegment from "./NavigationSegment";
import server_config from "../ServerConfiguration";
import SimpleResourceFieldViewer from "./SimpleResourceFieldViewer";
import CompositionDisplay from "../components/CompositionViewer";
import { editModeOff, editModeOn, selectEditMode } from "../slices/editMode/editModeSlice";
import { changeTabFocus, changeTabMenu, selectTabFocus, selectTabMenu } from "../slices/tabPane/tabPaneSlice";
import updateFhirJson from "../functions/updateFhirJsonAPI";
import sortResourceElementsFunction from "../functions/sortResourceElementsAPI";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";
import getFhirJson from "../functions/getFhirJsonAPI";
import { displayStringFromFHIRReference, SelectResourceFromSearchModal } from "../datatypeComponents/ReferenceEntry";
import ListDisplay from "./ListResourceDisplay";
import ResourceBottomDisplay from "./ResourceBottomDisplay";
import { changeTopNavigationElements, selectBottomNavigationElements, selectTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import { displayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import { displayStringFromFHIRPeriod } from "../datatypeComponents/PeriodEntry";
import { displayStringFromFHIRCoding } from "../datatypeComponents/CodingEntry";
import { displayStringFromFHIRExpression } from "../datatypeComponents/ExpressionEntry";
import { displayStringFromFHIRQuantity } from "../datatypeComponents/QuantityEntry";
import { displayStringFromFHIRRange } from "../datatypeComponents/RangeEntry";
import { displayStringFromFHIRRelativeTime } from "../datatypeComponents/RelativeTimeEntry";
import submitFhirJson from "../functions/submitFhirJsonAPI";
import ActivityDefinitionDisplay from "./ActivityDefinitionResourceDisplay";
import DocumentReferenceDisplay from "./DocumentReferenceResourceDisplay";
import PlanDefinitionDisplay from "./PlanDefinitionResourceDisplay";
import MeasureDisplay from "./MeasureResourceDisplay";
import LibraryDisplay from "./LibraryResourceDisplay";

const fevir_node_server_address = server_config["fevir_node_server_address"];

const handleDownload = (event, jsonString, resourceId) => {
    event.preventDefault();
    const blob = new Blob([jsonString], { type: 'text/json' });
    const a = document.createElement('a');
    a.download = 'FOI' + resourceId + '.json';
    a.href = window.URL.createObjectURL(blob);
    const clickEvt = new MouseEvent('click', { view: window, bubbles: true, cancelable: true });
    a.dispatchEvent(clickEvt);
    a.remove();
};

const ResourceTextDisplay = ({ resourceType, resourceId, fhirJson }) => {
    if (typeof (fhirJson) === "object" && fhirJson.resourceType === resourceType) {
        try {
            switch (resourceType) {
                case "ActivityDefinition":
                    return <ActivityDefinitionDisplay foi={resourceId} />;
                case "Composition":
                    return <CompositionDisplay foi={resourceId} />;
                case "DocumentReference":
                    return <DocumentReferenceDisplay foi={resourceId} />;
                case "Library":
                    return <LibraryDisplay foi={resourceId} />;
                case "List":
                    return <ListDisplay foi={resourceId} />;
                case "Measure":
                    return <MeasureDisplay foi={resourceId} />;
                case "PlanDefinition":
                    return <PlanDefinitionDisplay foi={resourceId} />;
                default:
                    return <div>
                    <div style={{ marginTop: "12px" }}>
                    </div>
                    <SimpleResourceFieldViewer resource={fhirJson} parentElement={""} startExpanded={true} />
                </div>
            }
        } catch (e) {
            return <>Invalid {resourceType} JSON. See JSON Tab to see the resource.</>
        }
    } else {
        return <></>
    }
}

const ResourceDisplay = ({ foi }) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const userId = useSelector(selectCurrentUserId);
    const userName = useSelector(selectCurrentUserName);
    const userIsAdmin = useSelector(selectCurrentUserAdmin);

    const editMode = useSelector(state => selectEditMode(state));
    const tabFocus = useSelector(state => selectTabFocus(state));
    const tabMenu = useSelector(state => selectTabMenu(state));
    const topNavigationElements = useSelector(selectTopNavigationElements);
    const bottomNavigationElements = useSelector(selectBottomNavigationElements);
    const navigationElements = topNavigationElements.concat(bottomNavigationElements);

    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceCollection = useSelector(state => selectAllResources(state));
    const unsavedChanges = useSelector(state => selectChangedResourcesListStatus(state));
    const changedResourcesList = useSelector(state => selectChangedResourcesList(state));
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));

    const editPermission = parentResourceDetail?.usageData?.editpermission;
    const readPermission = parentResourceDetail?.usageData?.readpermission;
    const adminPermission = parentResourceDetail?.usageData?.adminpermission;
    const resourceJson = parentResourceDetail?.revisedResourceJson;

    const [jsonViewEditingState, setJsonViewEditingState] = useState({
        "fhirEntryJson": {}, "fhirEntryString": "",
        "fhirEntryStringChange": false,
        "status": "active"
    });

    //TODO -- check if this should use revisedResourceJson instead and how this changes when changes are made
    useEffect(() => {
        if (resourceJson) {
            setJsonViewEditingState({
                "fhirEntryJson": resourceJson,
                "fhirEntryString": JSON.stringify(resourceJson),
                "fhirEntryStringChange": false,
                "status": "active" //TODO - replace with getting publicationStatus value from the parentResourceDetail
            });
            let resourceType = resourceJson.resourceType;

            if (resourceType === "Composition") { //TODO -- move this to CompositionViewer when changed to fit core model
                dispatch(changeTopNavigationElements(["Summary", "Table View", "Section Detail"]));
                dispatch(changeTabMenu(["Text View", "Section Detail", "JSON View", "Usage View"]));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourceJson]);

    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [loadingSortedElements, setLoadingSortedElements] = useState(false);

    let loading = loadingSubmit;

    //TODO -- determine how to set value of previousVersionLoaded
    let previousVersionLoaded = false;

    const CantDisplayResource = () => {
        let displayMessage = "";
        if (resourceJson?.resourceType) {
            if (userId) {
                displayMessage = "You do not have permission to view this resource.";
            } else {
                displayMessage = "Please log in to view resource.";
            }
        } else {
            displayMessage = "Resource not found";
        }
        return <span><h1>{displayMessage}<br /><br /></h1></span>
    }

    const ViewEditJSONButton = () => {
        return <Button className="formButton navigationButton"
            style={{ color: "#000000", width: "97%", margin: "0px" }}
            content={editMode ? "Edit JSON" : "View JSON"}
            onClick={() => { dispatch(changeTabFocus("JSON View")); }} />;
    };

    const getDisplayTitle = (fhirJson) => {
        if (fhirJson) {
            if (fhirJson.title && fhirJson.title.trim()) {
                return fhirJson.title.trim();
            }
            if (fhirJson.name) {
                if (typeof fhirJson.name === "string") {
                    if (fhirJson.name.trim()) {
                        return fhirJson.name.trim();
                    }
                } else if (Array.isArray(fhirJson.name) && fhirJson.name[0]?.text && fhirJson.name[0].text.trim()) {
                    return fhirJson.name[0].text.trim();
                }
            }
        }
        if (parentResourceDetail?.displayTitle) {
            return parentResourceDetail.displayTitle;
        }
        return "UNTITLED RESOURCE";
    }

    const publishAction = async () => {
        setLoadingSubmit(true);
        setJsonViewEditingState(prevState => { return { ...prevState, "status": "active" }; });

        let fhirJson = JSON.parse(jsonViewEditingState.fhirEntryString);
        if (fhirJson.status === "draft") {
            fhirJson.status = "active";
        }
        if (fhirJson.status === "preliminary") {
            fhirJson.status = "final";
        }
        await updateFhirJson(foi, fhirJson, getDisplayTitle(fhirJson), apiToken);
        setLoadingSubmit(false);
        navigate("/" + foi);
    }

    const PublishButton = () => {
        return <Button className="formButton navigationButton"
            style={{ color: "#000000", margin: "0px" }}
            content={"Publish"}
            onClick={() => { publishAction(); }}
            disabled={previousVersionLoaded}
        />;
    };

    const changeResourceViewTab = async (tabIndex, tabLabel, automated) => {
        let allowTabChange = true;
        let resourceType = resourceJson.resourceType;
        if (resourceType === "CodeSystem") {
            if (tabIndex === 1 && editMode) {
                if (window.confirm("You can't go to the Term Detail tab until you save or discard changes that you've made to the Resource. Click 'OK' to save your changes and then navigate away. Click 'Cancel' and then refresh the browser to discard the changes.")) {
                    //setLoadingSubmit(true);
                    //let submitResult = await submitUpdatedFhirResource(null, resourceType, resourceId, formInputsStateRef, fhirEntryState,
                    //  setFhirEntryState, globalContext, history, changeFormState, false, undefined);
                    //setLoadingSubmit(false);
                } else {
                    allowTabChange = false;
                }
            }
            if (tabIndex !== 1) {
                if (editMode) {// && (fhirEntryState.termChanged || fhirEntryState.newChildConcepts?.length > 0)) {
                    if (window.confirm("You can't leave this tab until you save or discard changes that you've made to the term. Click 'OK' to save your changes and then navigate away. Click 'Cancel' and then refresh the browser to discard the changes.")) {
                        //  setLoadingSubmit(true);
                        //  let submitResult = await submitUpdatedFhirResource(null, resourceType, resourceId, formInputsStateRef, fhirEntryState, setFhirEntryState, globalContext, history, changeFormState, false, undefined);
                        //  setLoadingSubmit(false);
                        allowTabChange = false;
                    } else {
                        allowTabChange = false;
                    }
                }
            }
            if (tabIndex === 3) {// && fhirEntryState.fhirEntryString) {
                /** 
                let fhirJson = JSON.parse(fhirEntryState.fhirEntryString);
                if (fhirJson.version === "pre-release") {
                  let termProgress;
                  try {
                    const usersBody = {
                      'functionid': 'gettermcountincodesystem',
                      'idToken': '',
                      'resourceid': resourceId
                    };
                    let response;
                    response = await submitToFevirServer(globalContext, 10000, usersBody, true, false);
                    if (response.success) {
                      termProgress = response.termcountinfo;
                    }
                  } catch (e) { }
                  setCodeSystemTermProgressState(prevState => {
                    return {
                      ...prevState,
                      termProgress: termProgress,
                      loading: false
                    };
                  });
                }
                */
            }
        }
        /*
         else if (editMode && resourceType === "ArtifactAssessment" && fhirEntryState.riskOfBiasAssessmentToolResource && tabIndex !== 1) {
          changeFhirEntryState(false, "editMode");
          setPageEditModeState("");
          if (automated) {
    
          } else {
            let updateResult = await submitUpdatedFhirResource(null, resourceType, resourceId, formInputsStateRef, fhirEntryState, setFhirEntryState, globalContext, history, changeFormState, false, undefined);
            if (updateResult) {
              alert("Saved your rating change to the server, taking you out of edit mode and bringing you to the Text View tab.");
            } else {
              tabIndex = 0;
              tabLabel = "Text View";
              alert("Taking you out of edit mode and bringing you to the Text View tab.");
            }
          }
        } else if (editMode && resourceType === "EvidenceVariable" && tabIndex !== 1 && formInputsStateRef?.current?.characteristicState?.termChanged) {
          //changeFhirEntryState(false, "editMode");
          setPageEditModeState("");
          if (automated) {
    
          } else {
            let doYouWantToSave = false;
            if (window.confirm("Do you want to save your change to criteria to the server? If not, please refresh this page to change tabs.")) {
              doYouWantToSave = true;
            }
            if (doYouWantToSave) {
              let updateResult = await submitUpdatedFhirResource(null, resourceType, resourceId, formInputsStateRef, fhirEntryState, setFhirEntryState, globalContext, history, changeFormState, false, addToast);
              if (updateResult) {
                alert("Saved your criteria change to the server, taking you out of edit mode and bringing you to the Text View tab.");
              } else {
                tabIndex = 0;
                tabLabel = "Text View";
                alert("Taking you out of edit mode and bringing you to the Text View tab.");
              }
            } else {
              allowTabChange = false;
            }
          }
        }
        */
        /*
        else if (editMode && resourceType === "Composition" && tabIndex !== 1 && 
            (fhirEntryState.sectionChanged || fhirEntryState.newChildSections?.length > 0)) {
            //changeFhirEntryState(false, "editMode");
            setPageEditModeState("");
            if (automated) {

            } else {
            let doYouWantToSave = false;
            if (window.confirm("Do you want to save your change to the section to the server? If not, please refresh this page to change tabs.")) {
                doYouWantToSave = true;
            }
            if (doYouWantToSave) {
                let updateResult = await submitUpdatedFhirResource(null, resourceType, resourceId, formInputsStateRef, fhirEntryState, setFhirEntryState, globalContext, history, changeFormState, false, addToast);
                if (updateResult) {
                alert("Saved your section change to the server, taking you out of edit mode and bringing you to the Text View tab.");
                } else {
                tabIndex = 0;
                tabLabel = "Text View";
                alert("Taking you out of edit mode and bringing you to the Text View tab.");
                }
            } else {
                allowTabChange = false;
            }
            }
        }
        */
        if (allowTabChange) {
            /*
                        if (tabLabel === "Text View" || tabLabel === "Metadata") {
                            loadProjectResourcesList(setFhirEntryState, resourceType, resourceId, false, globalContext);
                        } else if (tabLabel === "JSON View") {
                            await builderUpdateJson(resourceType, resourceId, formInputsStateRef, fhirEntryState, setFhirEntryState, globalContext);
                        } else if (tabLabel === "Usage View") {
                            await builderUpdateJson(resourceType, resourceId, formInputsStateRef, fhirEntryState, setFhirEntryState, globalContext);
                            loadUsageView(resourceType, resourceId, globalContext, fhirEntryState, setFhirEntryState);
                        } else if (tabLabel === "Term Detail") {
                            if (resourceType === "CodeSystem") {
                                if (fhirEntryState.conceptCode) {
                                    history.push(`/resources/CodeSystem/${resourceId}#${fhirEntryState.conceptCode}`);
                                }
                            }
                        } else if (tabLabel === "Characteristic Detail") {
                            if (resourceType === "EvidenceVariable") {
                                if (fhirEntryState.pathIndexes?.length > 0) {
                                    history.push(`/resources/EvidenceVariable/${resourceId}#${fhirEntryState.pathIndexes.toString()}`);
                                }
                            }
                        } else if (tabLabel === "Section Detail") {
                            if (resourceType === "Composition") {
                                if (fhirEntryState.conceptCode) {
                                    history.push(`/resources/Composition/${resourceId}#${fhirEntryState.sectionIndexPath}`);
                                }
                            }
                        }
                            */
            if (tabIndex !== 1 && (resourceType === "CodeSystem" || resourceType === "EvidenceVariable" || resourceType === "Composition")) {
                navigate(`/resources/${resourceType}/${resourceJson.id}`);
            }
            dispatch(changeTabFocus(tabMenu[tabIndex]));
        }
    };

    let activeTabIndex = () => {
        for (const index in tabMenu) {
            if (tabMenu[index] === tabFocus) {
                return parseInt(index);
            }
        }
        return 0;
    };

    const ViewTabPane = () => (<Tab className="viewTabPane"
        menu={{ secondary: true, pointing: true }}
        panes={tabMenu.map(item => { return { menuItem: item }; })}
        activeIndex={activeTabIndex()}
        onTabChange={(e, data) => {
            changeResourceViewTab(data.activeIndex, tabMenu[data.activeIndex], false);
        }}
    />);

    const ResourceStatusRadioButton = () => {

        let values = ["active", "draft"];
        let labels = ["Publish (anyone can view)", "Draft (only editors can view)"];

        if (userIsAdmin) {
            values.push("retired");
            labels.push("Retired");
        }

        return <>
            <p>Need to replace GenericToggleButtons with functional replacement</p>
            {/**  <GenericToggleButtons thin={true} boxes={true} togglable={false}
        values={values} labels={labels} fieldLabel={"status"}
        elementName={"status"} changeElementName={"fhirEntryStringChange"}
        startingValue={fhirEntryState.status} setter={setFhirEntryState}
      /> */}
            <br />
            <br />
        </>
    }

    const sortResourceElements = async () => {
        setLoadingSortedElements(true);
        let sourceJson = JSON.stringify(JSON.parse(jsonViewEditingState.fhirEntryString), null, 2);
        let sortResourceElementsResponse = await sortResourceElementsFunction(sourceJson, apiToken);
        setLoadingSortedElements(false);
        if (sortResourceElementsResponse.success) {
            if (sourceJson !== sortResourceElementsResponse.sortedJson) {
                if (sourceJson.length === sortResourceElementsResponse.sortedJson.length) {
                    if (sortResourceElementsResponse["non-spec-elements"]) {
                        console.log(sortResourceElementsResponse["non-spec-elements"]);
                        alert("Sort completed, but some possibly non-FHIR elements found: " + sortResourceElementsResponse["non-spec-elements"].map((entry) => { return Object.keys(entry) }).join(', '));
                    }
                } else {
                    alert("WARNING the sorted Resource is a different size than the original. That likely means some content has been lost. We DO NOT recommend saving these change.");
                }
                setJsonViewEditingState(prevState => {
                    return {
                        ...prevState,
                        fhirEntryString: sortResourceElementsResponse.sortedJson,
                        fhirEntryStringChange: true
                    };
                });
            } else {
                alert("No element order changes made.");
            }
        } else if (sortResourceElementsResponse.message) {
            alert("Unable to sort the order of the elements. " + sortResourceElementsResponse.error);
        } else {
            alert("Unable to sort the order of the elements.");
        }
    }

    const JSONViewResourceButtons = () => {
        return <div>
            {
                editMode
                    ?
                    <>
                        {userId ?
                            <>
                                <>
                                    <Button className="formButton" style={{ color: "#000000" }}
                                        content="Sort Element Order"
                                        onClick={() => { sortResourceElements(); }} disabled={loadingSortedElements} />
                                    {loadingSortedElements && <img style={{ height: "22px" }} src="/spinner.gif" alt="Loading" />}
                                </>
                                &nbsp;&nbsp;&nbsp;
                                {(jsonViewEditingState.fhirEntryJson?.status &&
                                    jsonViewEditingState.fhirEntryJson.status !== "draft" &&
                                    jsonViewEditingState.fhirEntryJson.status !== "preliminary" &&
                                    jsonViewEditingState.fhirEntryJson.status !== "retired") &&
                                    <>
                                        <Button className="formButton" style={{ color: "#000000" }}
                                            content="Edit Resource in JSON Tree Writer"
                                            onClick={() => {
                                                window.open("https://khalid-shahin.github.io/JSON-Tree-Writer/?server=" +
                                                    encodeURIComponent(fevir_node_server_address) +
                                                    "&extrarequestheader=functionid&extrarequestinfo=getfhirresource&responseobjectname=fhirjsonstring&string=true&resourcetype=" +
                                                    resourceJson.resourceType +
                                                    "&resourceid=" +
                                                    resourceJson.id, '_blank');
                                            }}
                                            disabled={previousVersionLoaded} />
                                        &nbsp;&nbsp;&nbsp;
                                    </>
                                }
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="Submit Updated Resource"
                                    onClick={submitUpdatedResource}
                                    disabled={!jsonViewEditingState.fhirEntryStringChange || previousVersionLoaded}
                                />
                            </>
                            :
                            <>&nbsp;&nbsp;&nbsp;Please log back in.</>
                        }
                    </>
                    :
                    <>
                        {userId && readPermission && editPermission ?
                            <>
                                &nbsp;&nbsp;&nbsp;
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="Edit Resource"
                                    onClick={() => { dispatch(editModeOn()); }}
                                    disabled={previousVersionLoaded} />
                                {jsonViewEditingState.status === "draft" &&
                                    <>
                                        &nbsp;&nbsp;&nbsp;
                                        <PublishButton />
                                    </>
                                }
                            </>
                            :
                            <></>
                        }
                        {resourceJson.resourceType &&
                            <>
                                &nbsp;&nbsp;&nbsp;
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="Clone Resource"
                                    onClick={() => {
                                        navigate("/create/" + resourceJson.resourceType + "/" + resourceJson.id);
                                    }}
                                    disabled={previousVersionLoaded} />
                            </>
                        }
                        &nbsp;&nbsp;&nbsp;
                        <Button className="formButton" style={{ color: "#000000" }}
                            content="Add New Resource"
                            onClick={() => { navigate("/create"); }} />
                        &nbsp;&nbsp;&nbsp;
                        <Button className="formButton" style={{ color: "#000000" }}
                            content="Download JSON"
                            onClick={event => handleDownload(event, jsonViewEditingState.fhirEntryString || '', foi)} />

                    </>
            }
        </div>
    }

    const saveAllChanges = async () => {
        const filteredResourceCollection = resourceCollection.filter(entry => changedResourcesList.includes(entry.id));
        let primaryEntryUpdated;
        for (const entry of filteredResourceCollection) {
            let entryFoi = entry.id;
            if (entryFoi === foi) {
                primaryEntryUpdated = entry;
            } else {
                let resourceJson = entry.revisedResourceJson;
                let title = entry.displayTitle;
                await updateFhirJson(entryFoi, resourceJson, title, apiToken);
            }
        }
        let resourceJson;
        let title;
        if (primaryEntryUpdated) {
            resourceJson = primaryEntryUpdated.revisedResourceJson;
            title = primaryEntryUpdated.displayTitle;
        } else {
            resourceJson = parentResourceDetail.revisedResourceJson;
            title = parentResourceDetail.displayTitle;
        }
        await updateFhirJson(foi, resourceJson, title, apiToken);
        dispatch(changesCleared());
    }

    const submitUpdatedResource = async () => {
        setLoadingSubmit(true);
        if (tabFocus === "Text View") {
            await saveAllChanges();
        } else if (tabFocus === "JSON View") {
            let fhirJson = JSON.parse(jsonViewEditingState.fhirEntryString);
            await updateFhirJson(foi, fhirJson, getDisplayTitle(fhirJson), apiToken);
        }
        setLoadingSubmit(false);
        dispatch(editModeOff());
    };

    const EditUpdateButton = () => {
        return <Button className="formButton navigationButton"
            style={{ color: "#000000", width: "97%", margin: "0px" }}
            positive={unsavedChanges === "hasChanges"}
            content={editMode ? "Save All Changes" : "Edit " + resourceJson.resourceType}
            onClick={() => {
                if (editMode) {
                    submitUpdatedResource();
                } else {
                    dispatch(editModeOn());
                }
            }}
            disabled={(!loadingSubmit && userId &&
                readPermission && editPermission) ? false : true}
        />;
    };

    const createChangeReport = async () => {
        let changeReportJson = {
            "resourceType": "ArtifactAssessment",
            "meta": { "profile": ["https://hl7.org/fhir/uv/ebm/StructureDefinition/adaptation"] },
            "extension": [
                {
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-status",
                    "valueCode": "active"
                },
                {
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-author",
                    "valueContactDetail": { "name": userName }
                }
            ],
            "title": userName + "'s Change Report for " + getDisplayTitle(resourceJson),
            "artifactReference": {
                "reference": "Composition/" + resourceJson.id,
                "type": "Composition",
                "display": getDisplayTitle(resourceJson)
            },
            "date": new Date().toISOString(),
            "content": []
        };
        for (const change of resourceChanges) {
            let originalValue = "";
            if (change.originalValue !== null && change.originalValue !== undefined) {
                if (typeof change.originalValue === "object") {
                    originalValue = JSON.stringify(change.originalValue);
                    if (change.originalDatatype === "CodeableConcept") {
                        originalValue = displayStringFromFHIRCodeableConcept(change.originalValue);
                    } else if (change.originalDatatype === "Period") {
                        originalValue = displayStringFromFHIRPeriod(change.originalValue);
                    } else if (change.originalDatatype === "Coding") {
                        originalValue = displayStringFromFHIRCoding(change.originalValue);
                    } else if (change.originalDatatype === "Expression") {
                        originalValue = displayStringFromFHIRExpression(change.originalValue);
                    } else if (change.originalDatatype === "Quantity") {
                        originalValue = displayStringFromFHIRQuantity(change.originalValue);
                    } else if (change.originalDatatype === "Range") {
                        originalValue = displayStringFromFHIRRange(change.originalValue);
                    } else if (change.originalDatatype === "Reference") {
                        originalValue = displayStringFromFHIRReference(change.originalValue);
                    } else if (change.originalDatatype === "RelativeTime") {
                        originalValue = displayStringFromFHIRRelativeTime(change.originalValue);
                    }
                } else if (typeof change.originalValue === "string") {
                    originalValue = change.originalValue;
                } else {
                    originalValue = change.originalValue.toString();
                }
            }
            let newValue = "";
            if (change.newValue !== null && change.newValue !== undefined) {
                if (typeof change.newValue === "object") {
                    newValue = JSON.stringify(change.newValue)
                    if (change.newDatatype === "CodeableConcept") {
                        newValue = displayStringFromFHIRCodeableConcept(change.newValue);
                    } else if (change.newDatatype === "Period") {
                        newValue = displayStringFromFHIRPeriod(change.newValue);
                    } else if (change.newDatatype === "Coding") {
                        newValue = displayStringFromFHIRCoding(change.newValue);
                    } else if (change.newDatatype === "Expression") {
                        newValue = displayStringFromFHIRExpression(change.newValue);
                    } else if (change.newDatatype === "Quantity") {
                        newValue = displayStringFromFHIRQuantity(change.newValue);
                    } else if (change.newDatatype === "Range") {
                        newValue = displayStringFromFHIRRange(change.newValue);
                    } else if (change.newDatatype === "Reference") {
                        newValue = displayStringFromFHIRReference(change.newValue);
                    } else if (change.newDatatype === "RelativeTime") {
                        newValue = displayStringFromFHIRRelativeTime(change.newValue);
                    }
                } else if (typeof change.newValue === "string") {
                    newValue = change.newValue;
                } else {
                    newValue = change.newValue.toString();
                }
            }
            let contentEntry = {
                "type": { "text": "adapted element" },
                "classifier": [{ "text": change.originalDatatype }],
                "path": ["Resource FOI " + change.resourceId, change.originalPath],
                "component": [
                    {
                        "summary": originalValue,
                        "type": {
                            "coding": [
                                {
                                    "system": "https://fevir.net/resources/CodeSystem/179423",
                                    "code": "OriginalResourceContent",
                                    "display": "Original Resource Content"
                                }
                            ]
                        }
                    },
                    {
                        "summary": newValue,
                        "type": {
                            "coding": [
                                {
                                    "system": "https://fevir.net/resources/CodeSystem/179423",
                                    "code": "SuggestedChange",
                                    "display": "Suggested Change to Resource Content"
                                }
                            ]
                        }
                    }
                ]
            };
            changeReportJson.content.push(contentEntry);
        }
        let response = await submitFhirJson(changeReportJson, changeReportJson.title, apiToken);
        let resourceFOI;
        if (response?.success) {
            resourceFOI = response.formstateid;
            alert("Change Report created -- view at https://fevir.net/" + resourceFOI);
        }
    }

    const CreateChangeReportButton = () => {
        return <Button className="formButton navigationButton"
            style={{ color: "#000000", width: "97%", margin: "0px" }}
            content="Create Change Report"
            onClick={() => { createChangeReport(); }}
            disabled={resourceChanges.length === 0} />;
    }



    const CloneButton = () => {
        return <Button className="formButton navigationButton"
            style={{ color: "#000000", width: "97%", margin: "0px" }}
            content={"Clone " + resourceJson.resourceType}
            onClick={() => {
                let redirectUrl = "/create";
                if (resourceJson.resourceType) {
                    redirectUrl += "/" + resourceJson.resourceType;
                }
                redirectUrl += "/" + resourceJson.id;
                navigate(redirectUrl)
            }}
            disabled={previousVersionLoaded} />;
    };

    let inviteText = "";
    //TODO -- need to recreate the invite functionality
    //    if (fhirEntryState.inviteCode) {
    //        inviteText = `You're invited by ${userName} to become an editor for the ${fhirEntryState.title} resource FOI ${foi}\nYou can accept this invite by going to this URL, be logged in, and then click the Accept button.\nhttps://fevir.net/invite/${fhirEntryState.inviteCode}`;
    //    }

    return <div>
        <div style={{ display: "flex", width: "100%" }}>
            <div>
                {(tabFocus === "Text View" ||
                    tabFocus === "Metadata" ||
                    tabFocus === "Track Changes") &&
                    <div style={{ float: "left", minWidth: "290px", marginRight: "18px" }}>
                        <div style={{ position: "absolute", width: "290px" }}>
                            {loading &&
                                <Dimmer className={"loadingDimmer"} active inverted>
                                    <Loader inverted>Loading</Loader>
                                </Dimmer>}
                            {resourceJson && <div>
                                <h3 style={{ fontSize: "1.1rem", marginTop: "0px", textAlign: "center" }}>
                                    {getDisplayTitle(resourceJson)}
                                </h3>
                                <NavigationSegment elements={navigationElements} resourceId={resourceJson.id}
                                    editor={editPermission} loading={loading} />
                                <div style={{ marginTop: "14px" }} />
                                <div>
                                    <div style={{ width: "100%", textAlign: "center", display: "flex" }}>
                                        <span>Comment Button GOES HERE</span>
                                        {/** 
                <CommentButton />
                */}
                                    </div>
                                    <div style={{ marginTop: "10px" }} />
                                    <div style={{ width: "100%", textAlign: "center", display: "flex" }}>
                                        {previousVersionLoaded ?
                                            <>{/*
                    <div style={{ width: "100%", textAlign: "center", width: "100%", textAlign: "left" }}>
                      <Button className="formButton navigationButton" style={{ color: "#000000", width: "100%", margin: "0px" }}
                        content="Revert to this Version"
                        disabled={true}
                      />
                    </div>*/}
                                            </>
                                            :
                                            <>
                                                <div style={{ width: "50%", textAlign: "left", float: "left" }}>
                                                    <EditUpdateButton />
                                                </div>
                                                <div style={{ width: "50%", float: "right", textAlign: "right" }}>
                                                    {editMode || resourceChanges.length > 0 ? <CreateChangeReportButton /> : <CloneButton />}
                                                </div>
                                            </>
                                        }
                                    </div>
                                    <div style={{ marginTop: "10px" }} />
                                    <div style={{ width: "100%", textAlign: "center", display: "flex" }}>
                                        <div style={{ width: "50%", float: "right", textAlign: "right" }}>
                                            <ViewEditJSONButton />
                                        </div>
                                    </div>
                                    {(!editMode && (resourceJson.status === "draft" ||
                                        resourceJson.status === "preliminary") && editPermission) &&
                                        <div style={{ width: "100%", textAlign: "center", display: "flex" }}>
                                            <div style={{ width: "100%", float: "left", textAlign: "left" }}>
                                                <div style={{ marginTop: "10px" }} />
                                                <PublishButton />
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>}
                        </div>
                    </div>
                }
                {tabFocus === "Term Detail" &&
                    <>
                        {/*
                    <TermDetailLeftSideDisplay firebase={firebase} loading={loading} robatTermLoad={robatTermLoad}
                        setLoadingSubmit={setLoadingSubmit}
                        fhirEntryState={fhirEntryState} setFhirEntryState={setFhirEntryState} formState={formState}
                        resourceId={resourceId} resourceType={resourceType} formInputsStateRef={formInputsStateRef}
                        changeFhirEntryState={changeFhirEntryState} submitUpdatedFhirResource={submitUpdatedFhirResource}
                        changeFormState={changeFormState} addToast={addToast} globalContext={globalContext}
                        history={history} setPageEditModeState={setPageEditModeState} setFormState={setFormState}
                        changeResourceViewTab={changeResourceViewTab} />
                        */}
                    </>
                }
                {tabFocus === "Section Detail" &&
                    <>
                        {/*
                    <SectionDetailLeftSideDisplay loading={loading} setLoadingSubmit={setLoadingSubmit}
                        fhirEntryState={fhirEntryState} setFhirEntryState={setFhirEntryState} formState={formState}
                        resourceId={resourceId} resourceType={resourceType} formInputsStateRef={formInputsStateRef}
                        changeFhirEntryState={changeFhirEntryState} submitUpdatedFhirResource={submitUpdatedFhirResource}
                        changeFormState={changeFormState} addToast={addToast} globalContext={globalContext}
                        history={history} setPageEditModeState={setPageEditModeState} />
                              */}
                    </>
                }
                {tabFocus === "Comparison Detail" &&
                    <>
                        {/*
                    <ComparisonDetailLeftSideDisplay loading={loading} setLoadingSubmit={setLoadingSubmit}
                        fhirEntryState={fhirEntryState} setFhirEntryState={setFhirEntryState} formState={formState}
                        resourceId={resourceId} resourceType={resourceType} formInputsStateRef={formInputsStateRef}
                        changeFhirEntryState={changeFhirEntryState} submitUpdatedFhirResource={submitUpdatedFhirResource}
                        changeFormState={changeFormState} addToast={addToast} globalContext={globalContext}
                        history={history} setPageEditModeState={setPageEditModeState} />
                          */}
                    </>
                }
                {tabFocus === "Characteristic Detail" &&
                    <>
                        {/*
                    <EvidenceVariableCharacteristicDetailLeftSideDisplay loading={loading} setLoadingSubmit={setLoadingSubmit}
                        fhirEntryState={fhirEntryState} setFhirEntryState={setFhirEntryState} formState={formState}
                        resourceId={resourceId} resourceType={resourceType} formInputsStateRef={formInputsStateRef}
                        changeFhirEntryState={changeFhirEntryState} submitUpdatedFhirResource={submitUpdatedFhirResource}
                        changeFormState={changeFormState} addToast={addToast} globalContext={globalContext}
                        history={history} setPageEditModeState={setPageEditModeState} />
                               */}
                    </>
                }
            </div>
            <div style={{ float: "right", width: "100%" }}>
                <Segment id="resourceTextViewSegment" name="resourceTextViewSegment"
                    className={`containerSegment remainderOfPageSegment ${editMode && "editSegment"} ${previousVersionLoaded && "historySegment"}`}
                    raised>
                    <>
                        {loading &&
                            <Dimmer className={"loadingDimmer"} active inverted>
                                <Loader inverted>Loading</Loader>
                            </Dimmer>
                        }
                    </>
                    <ViewTabPane />

                    {(loading || readPermission) ?
                        <></>
                        :
                        <CantDisplayResource />
                    }
                    {(resourceJson?.resourceType && resourceJson.resourceType !== "" &&
                        tabFocus === "Track Changes") && <><span>TODO</span>{/**
                        <ResourceTextDisplay resourceType={resourceType} resourceId={resourceId} getResource={getResource}
                            formInputsStateRef={formInputsStateRef}
                            setFhirEntryState={setFhirEntryState} changeFhirEntryState={changeFhirEntryState}
                            firebase={firebase} userState={userState}
                            changeFormState={changeFormState}
                            languageOptions={languageOptions}
                            setLanguageOptions={setLanguageOptions}
                            fhirEntryState={fhirEntryState} history={history} key="resourceTextDisplay"
                            referencedResourceState={referencedResourceState} setReferencedResourceState={setReferencedResourceState}
                            setChangeAvailableToSaveState={setChangeAvailableToSaveState} update={update}
                            resourceState={resourceState} setResourceState={setResourceState}
                            sourceJsonState={sourceJsonState} setSourceJsonState={setSourceJsonState} />
                             */}</>
                    }
                    {(resourceJson?.resourceType && resourceJson.resourceType !== "" &&
                        tabFocus === "Metadata") && <><span>TODO</span>{/**
                        <ResourceTextDisplay resourceType={resourceType} resourceId={resourceId} getResource={getResource}
                            formInputsStateRef={formInputsStateRef}
                            setFhirEntryState={setFhirEntryState} changeFhirEntryState={changeFhirEntryState}
                            firebase={firebase} userState={userState}
                            changeFormState={changeFormState}
                            languageOptions={languageOptions}
                            setLanguageOptions={setLanguageOptions}
                            fhirEntryState={fhirEntryState} history={history} key="resourceTextDisplay"
                            referencedResourceState={referencedResourceState} setReferencedResourceState={setReferencedResourceState}
                            setChangeAvailableToSaveState={setChangeAvailableToSaveState} update={update}
                            resourceState={resourceState} setResourceState={setResourceState}
                            sourceJsonState={sourceJsonState} setSourceJsonState={setSourceJsonState} />
                                 */}</>
                    }
                    {(resourceJson?.resourceType && resourceJson.resourceType !== "" &&
                        tabFocus === "Text View") &&
                        <>{resourceJson.resourceType === "CodeSystem" ?
                            <><span>TODO</span>{/**
                            <>
                                {fhirEntryState.editMode && <>
                                    {reorganizeConceptsModalState.modalOpen &&
                                        <ReorganizeSectionsModal resourceState={resourceState} setResourceState={setResourceState}
                                            reorganizeSectionsModalState={reorganizeConceptsModalState}
                                            setReorganizeSectionsModalState={setReorganizeConceptsModalState}
                                            update={updateCodeSystemTerm} elementName={"concept"} />}
                                    <Button style={{ color: "#000000", marginTop: "18px", marginLeft: "6px", fontSize: "18px", padding: "4px" }}
                                        className="formButton sectionReorganizeButton"
                                        content="Reorganize Concepts"
                                        onClick={() => { setReorganizeConceptsModalState(prevState => { return { ...prevState, modalOpen: true } }); }} />
                                    <br />
                                </>}
                                <ResourceTextDisplay resourceType={resourceType} resourceId={resourceId} getResource={getResource}
                                    formInputsStateRef={formInputsStateRef}
                                    setFhirEntryState={setFhirEntryState} changeFhirEntryState={changeFhirEntryState}
                                    firebase={firebase} userState={userState}
                                    changeFormState={changeFormState}
                                    languageOptions={languageOptions}
                                    setLanguageOptions={setLanguageOptions}
                                    fhirEntryState={fhirEntryState} history={history} key="resourceTextDisplay"
                                    referencedResourceState={referencedResourceState} setReferencedResourceState={setReferencedResourceState}
                                    setChangeAvailableToSaveState={setChangeAvailableToSaveState} update={update}
                                    resourceState={resourceState} setResourceState={setResourceState}
                                    sourceJsonState={sourceJsonState} setSourceJsonState={setSourceJsonState} />
                            </>
                                    */}</>
                            :
                            <>
                                <ResourceTextDisplay
                                    resourceType={resourceJson.resourceType}
                                    resourceId={resourceJson.id}
                                    fhirJson={resourceJson}
                                />
                                <br/>  <br/>
                                <ResourceBottomDisplay fhirJson={resourceJson} />
                            </>
                        }
                        </>
                    }
                    {tabFocus === "Term Detail" &&
                        <><span>TODO</span>{/**
                        <>{(resourceType === "ArtifactAssessment" && fhirEntryState.riskOfBiasAssessmentToolResource) ?
                            <RobatTermDisplay changeFormState={changeFormState}
                                formInputsStateRef={formInputsStateRef} resourceId={resourceId} fhirEntryState={fhirEntryState}
                                setFhirEntryState={setFhirEntryState} history={history} />
                            :
                            <CodeSystemTermDetailTabRouting formInputsStateRef={formInputsStateRef} resourceId={resourceId}
                                fhirEntryState={fhirEntryState} setFhirEntryState={setFhirEntryState} history={history}
                            />
                        }</>
                              */}</>
                    }
                    {tabFocus === "Comparison Detail" &&
                        <><span>TODO</span>{/**
                        <>{resourceType === "ArtifactAssessment" &&
                            <ComparisonDetailTabRouting formInputsStateRef={formInputsStateRef} resourceId={resourceId}
                                fhirEntryState={fhirEntryState} setFhirEntryState={setFhirEntryState} history={history} />
                        }</>
                              */}</>
                    }
                    {tabFocus === "Characteristic Detail" &&
                        <><span>TODO</span>{/**
                        <>{resourceType === "EvidenceVariable" &&
                            <EvidenceVariableCharacteristicDetailTabRouting formInputsStateRef={formInputsStateRef} resourceId={resourceId}
                                fhirEntryState={fhirEntryState} setFhirEntryState={setFhirEntryState} history={history}
                                changeFhirEntryState={changeFhirEntryState} submitUpdatedFhirResource={submitUpdatedFhirResource}
                                changeFormState={changeFormState} addToast={addToast} />
                        }</>
                              */}</>
                    }
                    {tabFocus === "Section Detail" &&
                        <><span>TODO</span>{/**
                        <SectionDetail formInputsStateRef={formInputsStateRef} resourceId={resourceId}
                            resourceState={resourceState} setResourceState={setResourceState}
                            sourceJsonState={sourceJsonState} setSourceJsonState={setSourceJsonState}
                            fhirEntryState={fhirEntryState} setFhirEntryState={setFhirEntryState} />
                                  */}</>
                    }
                    {tabFocus === "JSON View" &&
                        <>
                            {(resourceJson.resourceType && readPermission) &&
                                <>
                                    <h2 style={{ marginTop: "6px", marginBottom: "12px" }}>
                                        {getDisplayTitle(resourceJson)}
                                    </h2>
                                    <TextArea style={{ minHeight: "400px", width: "65%" }}
                                        value={JSON.stringify(JSON.parse(jsonViewEditingState.fhirEntryString), null, 2) || ''}
                                        onChange={(e) => {
                                            setJsonViewEditingState(prevState => {
                                                return {
                                                    ...prevState,
                                                    "fhirEntryString": e.target.value,
                                                    "fhirEntryStringChange": true
                                                }
                                            })
                                        }}
                                        readOnly={!editMode} />
                                    <br /><br />
                                    {editMode && <ResourceStatusRadioButton />}
                                </>
                            }
                            <JSONViewResourceButtons />
                            <br /><br /><br />
                        </>
                    }
                    {tabFocus === "Usage View" &&
                        <>
                            {(parentResourceDetail?.usageData && readPermission) &&
                                <>
                                    <h2 style={{ marginTop: "6px", marginBottom: "12px" }}>{parentResourceDetail.displayTitle}</h2>
                                    <br />
                                    <p>This resource has been accessed <b>{parentResourceDetail.usageData.totalviews}</b> times by at least <b>{parentResourceDetail.usageData.totalusersviewed}</b> users.</p>
                                    {(parentResourceDetail.usageData.totaledits > 0) && <p>This resource has been modified <b>{parentResourceDetail.usageData.totaledits}</b> times by at least <b>{parentResourceDetail.usageData.totalusersedited}</b> users.</p>}
                                    <br />
                                    {/** (fhirEntryState.startingVersionId > 1) && <>
                                        <p>TODO - Need to add VersionDifferenceViewer</p>
                                        {
                                        <VersionDifferenceViewer resourceId={resourceId} globalContext={globalContext} latestVersionId={parseInt(fhirEntryState.startingVersionId)}
                                            fhirEntryState={fhirEntryState} setFhirEntryState={setFhirEntryState} versionListState={versionListState} setVersionListState={setVersionListState} />
                                            }
                                    </>
                                    */}
                                    {/*adminPermission &&
                                        <>
                                            <div className="resourceListHeader" style={{ cursor: "default" }}>
                                                <div style={{ paddingTop: "20px" }}
                                                    className="resourceListHeaderText">
                                                    <span>Co-editors</span>
                                                </div>
                                            </div>
                                            {fhirEntryState.editors?.map((editor, editorIndex) => {
                                                return <h4 key={editorIndex} style={{ margin: "0px" }}>{editor.name}</h4>
                                            })}
                                            <br />
                                            {fhirEntryState.inviteCode &&
                                                <>
                                                    <div style={{ width: "100%", padding: "6px", background: "#FFFFFF", border: "1px solid #000000", whiteSpace: "pre-wrap" }}>{inviteText}</div>
                                                    <br />
                                                    <Button className="formButton" style={{ color: "#000000" }}
                                                        content="Copy Invite to Clipboard"
                                                        onClick={() => { navigator.clipboard.writeText(inviteText) }} />
                                                    <br /><br />
                                                    <Button className="formButton" style={{ color: "#000000" }}
                                                        content="Disable Invite-to-Edit Link"
                                                        onClick={() => {
                                                            if (window.confirm("Are you sure you want to disable this invite link to no longer add additional co-editors to this resource?")) {
                                                                //TODO -- add revokeInviteLink
                                                                //revokeInviteLink(resourceId, fhirEntryState.inviteCode, globalContext, setFhirEntryState);
                                                            }
                                                        }}
                                                        disabled={previousVersionLoaded}
                                                    />
                                                </>
                                            }
                                            {fhirEntryState.inviteNotCreatedYet &&
                                                <>
                                                    <Button className="formButton" style={{ color: "#000000" }}
                                                        content="Create Invite-to-Edit Link"
                                                        onClick={() => {
                                                            //TODO - add createInviteLink
                                                            //createInviteLink(resourceId, globalContext, setFhirEntryState);
                                                        }}
                                                        disabled={fhirEntryState.creatingInvite || fhirEntryState.previousVersionLoaded} />
                                                </>
                                            }
                                        </>
                                    */}
                                    <br /><br /><br />
                                </>
                            }
                        </>
                    }
                </Segment>
            </div >
        </div >
    </div >
}

const ResourceDisplayPage = ({ }) => {

    const { foi } = useParams();
    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const changesSaved = useSelector(selectChangedResourcesListStatus);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        const foi = e.currentTarget.foi.value;
        navigate("/" + foi);
    }

    const [resourceJsonLoaded, setResourceJsonLoaded] = useState(false);
    const [selectResourceFromSearchState, setSelectResourceFromSearchState] = useState({});

    const loadFhirJson = useCallback(async (FOI) => {
        let fhirJson = await getFhirJson(FOI, null, apiToken);
        if (fhirJson && typeof fhirJson === "object") {
            let productFamily = fhirJson.resourceType;
            if (fhirJson.resourceType === "Composition") {
                productFamily = fhirJson.type?.coding?.[0]?.code || fhirJson.type?.text || "Composition";
            }
            let body = {
                parentProductFoi: fhirJson.id, productFamily: productFamily,
                apiToken: apiToken, resourceType: fhirJson.resourceType, fhirJson: fhirJson
            };
            dispatch(fetchResourceCollection(body));
        } else {
            alert("No Resource found for that FOI.");
        }
    }, [apiToken, dispatch]);

    const loadTheFhirJson = useCallback(async (foi) => {
        await loadFhirJson(foi);
        setResourceJsonLoaded(true);
    }, [loadFhirJson, setResourceJsonLoaded]);

    useEffect(() => {
        if (changesSaved === "empty" && foi && !isNaN(foi) && apiToken) {
            loadTheFhirJson(foi);
        }
    }, [changesSaved, foi, loadTheFhirJson, apiToken]);

    return <>
        {
            resourceJsonLoaded ?
                <ResourceDisplay foi={foi} />
                :
                <div className="pagecontent">
                    <br /><br />
                    {!apiToken && <h1>Please sign in to use this function.</h1>}
                    <form onSubmit={handleSubmit}>
                        <label htmlFor="foi">Enter FOI</label>
                        <input type="text" id="foi" defaultValue="" required />
                        <button>Get Resource</button>
                    </form>
                    <br />
                    <Button className="formButton" style={{ color: "#000000" }}
                        content="Search Resources to Select One"
                        onClick={() => { setSelectResourceFromSearchState({ "modalOpen": true }); }} />
                    {selectResourceFromSearchState.modalOpen &&
                        <SelectResourceFromSearchModal
                            setSelectResourceFromSearchState={setSelectResourceFromSearchState} />}
                    <br />
                </div>
        }
    </>
}

export default ResourceDisplayPage